/**
 * Stale Audit Panel
 *
 * Diagnostic section on the Dashboard. Collapsed by default — clicking the
 * header expands it to show every open ticket's expected vs actual stale-
 * tracking state. Surfaces tickets where StaleTicketProcessor's pipeline
 * never advanced (e.g. day 41 in Waiting Customer with no nudge).
 *
 * Driven by GET /api/tickets/audit. Clicking a row calls the prop
 * `onOpenTicket(autoTaskId)` so the parent can show the existing
 * TicketDetailModal.
 */

import { useEffect, useState } from 'react';
import { getTicketsAudit, type AuditRow, type AuditSummary } from '../services/api';

interface Props {
  onOpenTicket: (autoTaskId: number) => void;
}

const ACTUAL_LABEL: Record<AuditRow['actualState'], string> = {
  untracked: 'no tracking',
  'tracked-no-flag': 'tracked (no flag)',
  snoozed: 'snoozed',
  flagged: 'flagged',
  nudged: 'nudged',
  closed: 'auto-closed',
  emailed: 'admin emailed',
};

const EXPECTED_LABEL: Record<AuditRow['expectedState'], string> = {
  healthy: 'healthy',
  wc_flagged: 'flagged',
  wc_nudged: 'nudged',
  wc_closed: 'auto-closed',
  wou_flagged: 'flagged',
  wou_emailed: 'admin emailed',
};

function tone(actual: AuditRow['actualState'], isAnomaly: boolean): string {
  if (actual === 'snoozed') return 'text-indigo-300 bg-indigo-900/30';
  if (isAnomaly) return 'text-red-300 bg-red-900/30';
  if (actual === 'nudged' || actual === 'emailed' || actual === 'flagged') return 'text-amber-300 bg-amber-900/20';
  return 'text-slate-400 bg-slate-800/50';
}

export function StaleAuditPanel({ onOpenTicket }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'anomalies' | 'all'>('anomalies');
  const [showSnoozed, setShowSnoozed] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await getTicketsAudit();
      setSummary(res.summary);
      setRows(res.rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (expanded && !summary) load();
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = rows.filter(r => {
    if (filter === 'anomalies' && !r.isAnomaly) return false;
    if (!showSnoozed && r.actualState === 'snoozed') return false;
    return true;
  });

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-white">Stale Audit</span>
          {summary && summary.anomalies > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-300 border border-red-500/40">
              {summary.anomalies} anomal{summary.anomalies === 1 ? 'y' : 'ies'}
            </span>
          )}
          {summary && summary.stuckBeyondClose > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-700/30 text-red-200 border border-red-600/40">
              {summary.stuckBeyondClose} past auto-close
            </span>
          )}
          <span className="text-xs text-slate-500">
            {summary ? `${summary.total} open` : (expanded ? 'loading…' : 'click to inspect tracking state')}
          </span>
        </div>
        <span className="text-slate-400 text-sm">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div className="border-t border-slate-700 p-4 space-y-3">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-sm text-red-300">{error}</div>
          )}

          {summary && (
            <div className="flex gap-2 text-xs flex-wrap">
              <Chip label="Total open" value={summary.total} />
              <Chip label="Anomalies" value={summary.anomalies} tone="red" />
              <Chip label="Past auto-close" value={summary.stuckBeyondClose} tone="red" />
              <Chip label="Missing nudge" value={summary.missingNudge} tone="amber" />
              <Chip label="Missing flag" value={summary.missingFlag} tone="amber" />
              <Chip label="Snoozed" value={summary.snoozed} tone="indigo" />
            </div>
          )}

          <div className="flex items-center gap-3 text-xs">
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setFilter('anomalies')}
                className={`px-2 py-1 rounded ${filter === 'anomalies' ? 'bg-red-500/30 text-red-200' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}
              >
                Anomalies only
              </button>
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={`px-2 py-1 rounded ${filter === 'all' ? 'bg-blue-500/30 text-blue-200' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}
              >
                All open
              </button>
            </div>
            <label className="flex items-center gap-1 text-slate-400">
              <input type="checkbox" checked={showSnoozed} onChange={e => setShowSnoozed(e.target.checked)} />
              show snoozed
            </label>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="ml-auto px-2 py-1 rounded bg-slate-700 text-slate-300 hover:bg-slate-600 disabled:opacity-50"
            >
              {loading ? 'refreshing…' : 'refresh'}
            </button>
          </div>

          {visible.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">
              {loading ? 'Loading…' : (filter === 'anomalies' ? '🎉 no anomalies — all open tickets are at their expected stale-tracking tier' : 'no tickets in scope')}
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-800 border-b border-slate-700">
                  <tr className="text-slate-400 text-left">
                    <th className="py-2 pr-2 font-medium">Ticket</th>
                    <th className="py-2 pr-2 font-medium">Company</th>
                    <th className="py-2 pr-2 font-medium">Title</th>
                    <th className="py-2 pr-2 font-medium">Status</th>
                    <th className="py-2 pr-2 font-medium text-right">Day</th>
                    <th className="py-2 pr-2 font-medium">Expected</th>
                    <th className="py-2 pr-2 font-medium">Actual</th>
                    <th className="py-2 pr-2 font-medium">Auto-close</th>
                    <th className="py-2 pr-2 font-medium">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => (
                    <tr
                      key={r.autoTaskId}
                      onClick={() => onOpenTicket(r.autoTaskId)}
                      className={`border-b border-slate-700/50 cursor-pointer hover:bg-slate-700/40 ${r.isAnomaly ? 'bg-red-900/10' : ''}`}
                    >
                      <td className="py-1.5 pr-2 font-mono text-blue-300">{r.ticketNumber}</td>
                      <td className="py-1.5 pr-2 text-slate-300 max-w-[140px] truncate" title={r.companyName}>{r.companyName}</td>
                      <td className="py-1.5 pr-2 text-slate-400 max-w-[260px] truncate" title={r.title}>{r.title}</td>
                      <td className="py-1.5 pr-2 text-slate-400">{r.status}</td>
                      <td className={`py-1.5 pr-2 text-right tabular-nums font-medium ${r.daysInStatus >= 10 ? 'text-red-300' : r.daysInStatus >= 5 ? 'text-amber-300' : 'text-slate-400'}`}>{r.daysInStatus}d</td>
                      <td className="py-1.5 pr-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-700 text-slate-300">{EXPECTED_LABEL[r.expectedState]}</span>
                      </td>
                      <td className="py-1.5 pr-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${tone(r.actualState, r.isAnomaly)}`}>{ACTUAL_LABEL[r.actualState]}</span>
                      </td>
                      <td className="py-1.5 pr-2">
                        {(() => {
                          if (!r.autoCloseAfterDate) return <span className="text-slate-600">—</span>;
                          const closeOn = new Date(r.autoCloseAfterDate);
                          const days = Math.ceil((closeOn.getTime() - Date.now()) / 86400000);
                          const dateStr = closeOn.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                          if (days < 0) return <span className="text-slate-500 text-[10px]">past ({dateStr})</span>;
                          if (days === 0) return <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-500/30 text-red-300 border border-red-400/50">closes today</span>;
                          const warned = !!r.autoCloseWarnedAt;
                          return (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] border ${warned ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' : 'bg-purple-500/20 text-purple-300 border-purple-500/30'}`}
                              title={`Auto-close ${closeOn.toLocaleDateString()} (${days}d)${warned ? ' — customer warned' : ''}`}
                            >
                              🔒 {days}d ({dateStr})
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-1.5 pr-2 text-slate-400 text-[11px] max-w-[420px]">{r.anomalyReason || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, tone }: { label: string; value: number; tone?: 'red' | 'amber' | 'indigo' }) {
  const cls =
    tone === 'red' ? 'bg-red-500/15 text-red-300 border-red-500/30' :
    tone === 'amber' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
    tone === 'indigo' ? 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' :
    'bg-slate-700 text-slate-300 border-slate-600';
  return (
    <span className={`px-2 py-1 rounded border ${cls} flex items-center gap-1.5`}>
      <span className="text-slate-400">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </span>
  );
}
